<template>
  <div class="tw:grid tw:min-h-dvh tw:bg-white tw:lg:h-dvh tw:lg:grid-cols-[1fr_560px]">
    <!-- min-w-0 keeps the grid column from growing past the viewport to fit the header -->
    <div
      class="tw:flex tw:min-w-0 tw:flex-col tw:justify-between tw:gap-10 tw:lg:overflow-y-auto tw:px-6 tw:py-8 tw:lg:px-16 tw:lg:py-12">
      <header class="tw:flex tw:items-center tw:justify-between tw:gap-4">
        <div class="tw:flex tw:items-center tw:gap-2.5">
          <BrandLogo :size="20">
            <span class="tw:text-16 tw:font-bold tw:tracking-[0.06em] tw:text-gray-900">{{ brand.shortName }}</span>
          </BrandLogo>
          <span class="tw:hidden tw:h-3.5 tw:w-px tw:bg-border-secondary tw:sm:block"></span>
          <span class="tw:hidden tw:text-13 tw:text-muted tw:sm:inline">{{ $t('auth.tagline') }}</span>
        </div>
        <!-- the selects shrink rather than push the header past a narrow viewport -->
        <div class="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
          <BrandSwitcher v-if="brandSwitcherEnabled" class="tw:w-44 tw:min-w-0" data-testid="auth-brand-switcher" />
          <LanguageSelect class="tw:w-56 tw:min-w-0" data-testid="auth-language-select" />
        </div>
      </header>

      <main class="tw:mx-auto tw:flex tw:w-full tw:max-w-[400px] tw:flex-col tw:gap-6">
        <slot />
      </main>

      <footer class="tw:text-12 tw:text-muted" data-testid="auth-footer">
        © {{ year }} {{ brand.legalName }}
        <template v-for="link in legalLinks" :key="link.key">
          ·
          <a :href="link.href" class="tw:auth-link" :target="link.href === '#' ? undefined : '_blank'" rel="noopener">{{
            $t(link.label)
          }}</a>
        </template>
      </footer>
    </div>

    <AuthBrandPanel class="tw:hidden tw:lg:flex" />
  </div>
</template>

<script setup lang="ts">
import { brand, brandSwitcherEnabled } from '@/config/brands';
import { resolveLegalLink } from '@/config/brands/legalLinks';
import { currentLanguage } from '@/locale/translator';
import BrandSwitcher from '@/components/generic/BrandSwitcher.vue';
import BrandLogo from '@/components/generic/BrandLogo.vue';
import LanguageSelect from '@/components/generic/LanguageSelect.vue';
import AuthBrandPanel from './AuthBrandPanel.vue';

const year = new Date().getFullYear();

// a link without a configured URL is hidden
const legalLinks = [
  { key: 'privacy', label: 'auth.footer.privacy', href: resolveLegalLink(brand.legalLinks.privacy, currentLanguage) },
  { key: 'terms', label: 'auth.footer.terms', href: resolveLegalLink(brand.legalLinks.terms, currentLanguage) },
  { key: 'imprint', label: 'auth.footer.imprint', href: resolveLegalLink(brand.legalLinks.imprint, currentLanguage) },
].filter((link): link is { key: string; label: string; href: string } => Boolean(link.href));
</script>
